import { PencilSquareIcon } from "@heroicons/react/24/outline";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import { useState } from "react";
import { toast } from "react-hot-toast";
import { twMerge } from "tailwind-merge";

import { useUpdateActiveWatchlist } from "@/features/watchlist/services/update-active-watchlist";
import { useLang } from "@/hooks/use-lang";

import { HeaderPopper } from "../watch-list-popup-menu/HeaderPopper";
import { HeaderTitle } from "./HeaderTitle";
import { WatchlistNameEditInput } from "./WatchlistNameEditInput";

export const WatchListHeader = ({ disabled }: { disabled: boolean }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isPopperOpen, setIsPopperOpen] = useState(false);
  const { mutate: updateActiveWatchlist } = useUpdateActiveWatchlist();
  const { t } = useLang();

  const submitRename = ({ newName }: { newName: string }) => {
    updateActiveWatchlist(
      { watchlistName: newName },
      {
        onSuccess: () => {
          toast.success(
            t.layout.watchlist.renameWatchlistSuccessToast(newName)
          );
        },
        onError: () => {
          toast.error(t.layout.watchlist.renameWatchlistFailToast);
        },
      }
    );
  };

  const handleWatchlistItemClick = () => {
    if (disabled) return;
    setIsPopperOpen((prev) => !prev);
  };

  const handleEditButtonClick = () => {
    if (disabled) return;
    setIsEditing(!isEditing);
  };

  return (
    <div className="row-span-1 flex items-center justify-between gap-x-3 py-6 pl-4 pr-3">
      {isEditing ? (
        <WatchlistNameEditInput
          submitRename={submitRename}
          setIsEditing={setIsEditing}
        />
      ) : (
        <>
          {/* watchlist name */}
          <ClickAwayListener onClickAway={() => setIsPopperOpen(false)}>
            <div className="relative w-full">
              <div
                role="button"
                className={twMerge(
                  "flex w-full cursor-pointer items-center justify-between rounded-md border border-zinc-200 bg-white px-3 py-2",
                  disabled && "cursor-not-allowed"
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  handleWatchlistItemClick();
                }}
                aria-label="open watchlist popup menu"
              >
                <HeaderTitle disabled={disabled} />
                <ChevronDownIcon className="h-4 w-4 fill-zinc-500" />
              </div>
              {isPopperOpen && (
                <HeaderPopper setIsPopperOpen={setIsPopperOpen} />
              )}
            </div>
          </ClickAwayListener>
          {/* edit button */}
          <button
            className={twMerge(
              "group flex shrink-0 items-center rounded-md border-none bg-transparent p-1 transition",
              disabled
                ? "cursor-not-allowed"
                : "cursor-pointer hover:bg-sky-100"
            )}
            aria-label="edit watchlist name"
            onClick={handleEditButtonClick}
          >
            <PencilSquareIcon
              className={twMerge(
                "h-6 w-6 text-zinc-600",
                disabled
                  ? "cursor-not-allowed text-zinc-300"
                  : "group-hover:text-sky-700"
              )}
            />
          </button>
        </>
      )}
    </div>
  );
};
